import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';
import { renderActionMenuItems } from './actionMenuUtils';
import './DesktopActionMenu.css';

/**
 * Desktop-only actions menu – shows a label (e.g. "Actions") that opens a dropdown.
 * Visible from md breakpoint up (d-none d-md-inline-block). Use alongside MobileActionMenu in layout headers.
 */
const DesktopActionMenu = ({
  items = [],
  className = '',
  toggleClassName = '',
  toggleLabel = 'Actions',
  maxInlineActions = 3,
}) => {
  const [show, setShow] = useState(false);

  if (!items || items.length === 0) return null;

  const actionableItems = items.filter((item) => item && !item.divider);
  const useInline = maxInlineActions > 0 && actionableItems.length > 0 && actionableItems.length <= maxInlineActions;

  const toggleClasses = [
    'btn',
    'dropdown-toggle',
    'dropdown-val',
    'dropddown_align',
    'layout-desktop-action-menu-toggle',
    toggleClassName,
  ]
    .filter(Boolean)
    .join(' ');

  const handleClose = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setShow(false);
    const preventNextTap = (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      document.removeEventListener('click', preventNextTap, true);
      document.removeEventListener('touchend', preventNextTap, true);
    };
    document.addEventListener('click', preventNextTap, true);
    document.addEventListener('touchend', preventNextTap, true);
    setTimeout(() => {
      document.removeEventListener('click', preventNextTap, true);
      document.removeEventListener('touchend', preventNextTap, true);
    }, 400);
  };

  if (useInline) {
    return (
      <div className={`d-none d-md-inline-flex layout-desktop-action-menu layout-desktop-action-menu--inline ${className}`.trim()}>
        {actionableItems.map((item, index) => {
          const key = `inline-${index}-${item.label}`;
          const btnClass = `btn btn-sm btn-outline-primary layout-desktop-action-menu-inline-btn`;

          if (item.to) {
            const to = typeof item.to === 'string' ? { pathname: item.to } : item.to;
            return (
              <Link key={key} to={to} className={btnClass}>
                {item.icon ? <img src={item.icon} alt="" className="layout-action-menu-item-icon" /> : null}
                {item.label}
              </Link>
            );
          }

          return (
            <button
              key={key}
              type="button"
              className={btnClass}
              onClick={item.onClick}
            >
              {item.icon ? <img src={item.icon} alt="" className="layout-action-menu-item-icon" /> : null}
              {item.label}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div className={`d-none d-md-inline-block layout-desktop-action-menu ${className}`.trim()}>
      {show && (
        <div
          className="layout-desktop-action-menu-backdrop"
          onClick={handleClose}
          onTouchStart={(e) => e.preventDefault()}
          onTouchEnd={handleClose}
          aria-hidden
        />
      )}
      <Dropdown
        alignRight
        className="layout-desktop-action-menu-dropdown"
        show={show}
        onToggle={(isOpen) => setShow(isOpen)}
      >
        <Dropdown.Toggle id="layout-desktop-action-menu-toggle" className={toggleClasses} aria-haspopup="true">
          {toggleLabel}
        </Dropdown.Toggle>
        <Dropdown.Menu className="layout-desktop-action-menu-menu">
          {renderActionMenuItems(items)}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

DesktopActionMenu.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        onClick: PropTypes.func,
      }),
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        to: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      }),
      PropTypes.shape({ divider: PropTypes.bool }),
    ])
  ),
  className: PropTypes.string,
  toggleClassName: PropTypes.string,
  toggleLabel: PropTypes.string,
  maxInlineActions: PropTypes.number,
};

export default DesktopActionMenu;
